import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Linkedin } from "lucide-react";
import { Logo } from "./Header";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO, ADDRESS, ORAI_BRANCH, SOCIALS } from "../lib/site";

const SOCIAL_ICONS = { Facebook, Instagram, X: Twitter, LinkedIn: Linkedin };

const cols = [
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Contact Us", to: "/contact" },
      { label: "Why PocketBima", to: "/#why" },
      { label: "Blog", to: "/blog" },
    ],
  },
  {
    title: "Insurance",
    links: [
      { label: "Motor Insurance", to: "/motor-insurance" },
      { label: "Health Insurance", to: "/health-insurance" },
      { label: "Life Insurance", to: "/life-insurance" },
      { label: "Term Insurance", to: "/life-insurance" },
      { label: "Business Insurance", to: "/business-insurance" },
      { label: "Travel Insurance", to: "/travel-insurance" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Contact", to: "/contact" },
      { label: "Get a Quote", to: "/contact" },
      { label: "FAQs", to: "/faq" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms & Conditions", to: "/terms-and-conditions" },
      { label: "Disclaimer", to: "/disclaimer" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-navy pb-28 pt-20 text-white md:pb-20" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_2fr]">
          <div>
            <Logo dark />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">
              Chhoti Pocket, Badi Suraksha. Simple, reliable and affordable insurance solutions
              for you, your family, your vehicle and your business.
            </p>
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.label];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`PocketBima on ${s.label}`}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white/70 transition-all duration-200 hover:-translate-y-0.5 hover:bg-mint hover:text-white"
                    data-testid={`social-${s.label.toLowerCase()}`}
                  >
                    <Icon className="h-4.5 w-4.5" />
                  </a>
                );
              })}
            </div>
            <div className="mt-7 space-y-3 text-sm">
              <a href={PHONE_TEL} className="flex items-center gap-3 text-white/80 transition-colors hover:text-mint" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-mint" /> {PHONE_DISPLAY}
              </a>
              <a href={EMAIL_MAILTO} className="flex items-center gap-3 text-white/80 transition-colors hover:text-mint" data-testid="footer-email">
                <Mail className="h-4 w-4 text-mint" /> {EMAIL}
              </a>
              <p className="flex items-start gap-3 text-white/60" data-testid="footer-address">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                <span><span className="font-semibold text-white/75">Head Office:</span> {ADDRESS}</span>
              </p>
              <p className="flex items-start gap-3 text-white/60" data-testid="footer-branch-address">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                <span><span className="font-semibold text-white/75">{ORAI_BRANCH.name}:</span> {ORAI_BRANCH.address}</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
            {cols.map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-mint">{col.title}</h4>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        className="text-sm text-white/65 transition-colors duration-150 hover:text-white"
                        data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="max-w-4xl text-xs leading-relaxed text-white/40" data-testid="footer-disclaimer">
            PocketBima provides insurance solutions and assistance. PocketBima is not an insurance
            company or insurer. Policy availability, coverage, premiums, terms and conditions are
            subject to the respective insurer's policy terms and applicable regulations. Please
            read policy documents carefully before concluding a sale.
          </p>
          <p className="mt-5 text-xs text-white/50" data-testid="footer-copyright">
            © 2026 Pocket Bima Insurance Service. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
